import { Component, Inject, OnInit, Input, Output, EventEmitter, signal, computed } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FeatureFlagsAdminService } from '../../services/feature-flags-admin.service';
import { FeatureFlag, Status } from '../../../models/feature-flag.model';
import { catchError, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Component for displaying and filtering a list of feature flags
 */
@Component({
  selector: 'cbre-feature-flags-list',
  templateUrl: './feature-flags-list.component.html',
  styleUrls: ['./feature-flags-list.component.scss'],
  standalone: false,
})
export class FeatureFlagsListComponent implements OnInit {
  /**
   * Default status filter to apply
   * @default 'active'
   */
  @Input() defaultFilterStatus: 'active' | 'archived' | 'all' = 'active';

  /**
   * Event emitted when a feature flag is selected
   */
  @Output() flagSelected = new EventEmitter<string>();

  /**
   * Event emitted when the create flag button is clicked
   */
  @Output() createFlag = new EventEmitter<void>();

  /**
   * Filters
   */
  statusFilter = new FormControl('active');
  searchFilter = new FormControl('');

  /**
   * Feature flags signal
   */
  featureFlags = signal<FeatureFlag[]>([]);

  /**
   * Computed signal to check if the list has any flags
   */
  hasFlags = computed(() => this.featureFlags().length > 0);

  /**
   * Flag to indicate loading state
   */
  loading = signal(true);

  /**
   * Column to sort by
   */
  sortColumn = 'updatedAt';

  /**
   * Sort direction
   */
  sortDirection: 'asc' | 'desc' = 'desc';

  /**
   * Original feature flags list
   */
  private originalFlags: FeatureFlag[] = [];

  constructor(
    private featureFlagsService: FeatureFlagsAdminService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  ngOnInit(): void {
    this.statusFilter.setValue(this.defaultFilterStatus);

    // Set up search filter change handler
    this.searchFilter.valueChanges.subscribe(() => this.filterAndSortFlags());

    // Trigger initial load
    this.refreshList();
  }

  /**
   * Refresh the feature flags list
   */
  refreshList(): void {
    // Set loading state before starting the fetch operation
    this.loading.set(true);

    const status = this.statusFilter.value === 'all' ? undefined : (this.statusFilter.value as Status);

    this.featureFlagsService
      .getAllFeatureFlags(status)
      .pipe(
        // Handle empty results or errors
        catchError(err => {
          this.logger.error(`Error fetching feature flags: ${err}`);
          return of([]);
        }),
        // Always ensure loading state is reset regardless of success/error
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe(flags => {
        // Store the raw flags data
        this.originalFlags = flags; // Keep a copy of the original flags
        this.featureFlags.set(this.filterAndSortFlagsInternal(flags));
      });
  }

  /**
   * Filter and sort flags based on current search term and sort settings
   */
  private filterAndSortFlags(): void {
    const searchTerm = this.searchFilter.value || '';

    if (!searchTerm.trim()) {
      // If search term is empty, restore the original list
      this.featureFlags.set(this.filterAndSortFlagsInternal(this.originalFlags));
      return;
    }

    const filteredSortedFlags = this.filterAndSortFlagsInternal(this.originalFlags);
    this.featureFlags.set(filteredSortedFlags);
  }

  /**
   * Filter and sort flags without updating the signal
   */
  private filterAndSortFlagsInternal(flags: FeatureFlag[]): FeatureFlag[] {
    const searchTerm = this.searchFilter.value || '';

    if (!searchTerm) {
      return this.sortFlags(flags);
    }

    const term = searchTerm.toLowerCase();
    return this.sortFlags(
      flags.filter(
        flag =>
          flag.id.toLowerCase().includes(term) ||
          flag.name.toLowerCase().includes(term) ||
          flag.description.toLowerCase().includes(term)
      )
    );
  }

  /**
   * Handle status filter change
   */
  onStatusFilterChange(): void {
    this.refreshList();
  }

  /**
   * Select a feature flag
   */
  onSelectFlag(id: string): void {
    this.flagSelected.emit(id);
  }

  /**
   * Create a new feature flag
   */
  onCreateFlag(): void {
    this.createFlag.emit();
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  /**
   * Sort the feature flags based on the current sort column and direction
   */
  private sortFlags(flags: FeatureFlag[]): FeatureFlag[] {
    return [...flags].sort((a, b) => {
      const factor = this.sortDirection === 'asc' ? 1 : -1;

      switch (this.sortColumn) {
        case 'id':
          return factor * a.id.localeCompare(b.id);
        case 'name':
          return factor * a.name.localeCompare(b.name);
        case 'updatedAt':
          return factor * (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
        case 'status':
          return factor * a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
  }

  /**
   * Set the sort column and direction
   */
  setSortColumn(column: string): void {
    if (this.sortColumn === column) {
      // Toggle direction if already sorting by this column
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    // Apply new sorting to current flags
    this.filterAndSortFlags();
  }

  /**
   * Get the sort direction indicator
   */
  getSortIndicator(column: string): string {
    if (column !== this.sortColumn) {
      return '';
    }
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }
}
